import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { TYPES } from "core/services/monitorService.js";
import metaStoreService from "core/services/metaStoreService/meta-store-service.js";
import api from "core/api/api";

import { Grid, CircularProgress } from "@material-ui/core";
import AppsMonitorTile from "./monitor_tiles/apps_monitor_tile";
import CPUMonitorTile from "./monitor_tiles/cpu_monitor_tile";
import MemMonitorTile from "./monitor_tiles/mem_monitor_tile";
import NodesMonitorTile from "./monitor_tiles/nodes_monitor_tile";
import getMonitorData from "./system_health_monitor_service";
import config from "../../../../config";
import useStyles from "./system_health_monitor.styles";

const SystemHealthMonitor = ({ setUpdatedTime }) => {
    const classes = useStyles();
    // declare all states
    const [loaded, setLoaded] = useState(false);
    const [cpuRate, setCpuRate] = useState(0);
    const [memRate, setMemRate] = useState(0);
    const [numRunningNodes, setNumRunningNodes] = useState(0);
    const [ttlNodes, setTtlNodes] = useState(0);
    const [nodeData, setNodeData] = useState([]);
    const [ttlApps, setTtlApps] = useState(0);
    const [appsData, setAppsData] = useState({});

    // set monitoring data and update states
    const setMonitorData = model => {
        setCpuRate(model?.cpuRate);
        setMemRate(model?.memRate);
        setNumRunningNodes(model?.numRunningNodes);
        setTtlNodes(model?.ttlNodes);
        setNodeData(model?.nodeData);
        setTtlApps(model?.ttlApps);
        setAppsData({
            terminated: model?.numTerminatedApps,
            running: model?.numRunningApps,
            halted: model?.numHaltedApps,
            others: model?.numOthersApps,
            otherStatusList: model?.otherAppsList
        });
        setUpdatedTime(Date.now());
    };

    const fetchResponse = async () => {
        // get id Strings for nodes, apps
        let objectIdentifiers = await metaStoreService.fetchIdentifiersByTypes(["SERVER", "APPLICATION"]);
        let idStrings = [];
        objectIdentifiers.forEach(objectIdentifier => {
            idStrings.push(objectIdentifier.get("id"));
        });
        // get data for ids
        return await api.getMonitoringStatsForComponentsWithoutTableInfo(idStrings, TYPES.FULL);
    };

    useEffect(() => {
        let intervalId;
        let cancelled = false;

        const updateData = async () => {
            try {
                const apiResponse = await fetchResponse();
                // component may have unmounted while the initial request was in flight
                if (cancelled) return;
                setMonitorData(getMonitorData(apiResponse));
                // set loaded to true to display data
                if (!loaded) {
                    setLoaded(true);
                }
            } catch (err) {
                if (!cancelled) {
                    console.error("Failed to fetch system health data", err);
                }
            }
        };
        updateData();
        // update data every 5 mins
        intervalId = setInterval(updateData, config.time.homepageIntervalMontoringData);
        // cleanUp fn to remove interval callback
        return () => {
            cancelled = true;
            clearInterval(intervalId);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * View
     */
    if (loaded) {
        return (
            <Grid item container className={classes.monitorContainer}>
                <Grid item xs>
                    <AppsMonitorTile total={ttlApps} appsData={appsData} />
                </Grid>
                <Grid item xs>
                    <CPUMonitorTile value={cpuRate} data={nodeData} />
                </Grid>
                <Grid item xs>
                    <MemMonitorTile value={memRate} data={nodeData} />
                </Grid>
                <Grid item xs>
                    <NodesMonitorTile running={numRunningNodes} total={ttlNodes} data={nodeData} />
                </Grid>
            </Grid>
        );
    }
    return <CircularProgress />;
};
export default SystemHealthMonitor;

SystemHealthMonitor.propTypes = {
    setUpdatedTime: PropTypes.func.isRequired
};
